// Translation selectors. Shared across pages to read from the translation
// cache populated by the App prefetch effect.
//
// Usage:
//   const { isSpanish, translations } = useApp()
//   const name = tr(isSpanish, translations, 'recipes', recipe.id, 'name', recipe.name)
//
// When isSpanish is false, these always short-circuit to the English fallback
// so the English path is byte-identical to pre-feature behavior.

export function tr(isSpanish, translations, kind, id, field, fallback) {
  if (!isSpanish) return fallback
  if (id === undefined || id === null) return fallback
  const entry = translations?.[kind]?.[String(id)]
  if (!entry) return fallback
  const value = entry[field]
  return value === undefined || value === null || value === '' ? fallback : value
}

// Translate a recipe ingredient name.
// Keyed by the English ingredient name (what the frontend always has),
// since the recipes API doesn't return ingredient_id in its rows.
export function trIngredient(isSpanish, translations, recipeId, ingredientName, fallback) {
  if (!isSpanish) return fallback
  if (!ingredientName) return fallback
  if (recipeId !== undefined && recipeId !== null) {
    const entry = translations?.recipes?.[String(recipeId)]
    const translated = entry?.ingredient_names?.[ingredientName]
    if (translated) return translated
  }
  // The same kitchen component can appear without a recipe link. Search the
  // already-loaded recipe dictionaries before using the built-in kitchen
  // glossary, so vegetables/proteins still translate everywhere they appear.
  for (const entry of Object.values(translations?.recipes || {})) {
    const translated = entry?.ingredient_names?.[ingredientName]
    if (translated) return translated
  }
  return KITCHEN_INGREDIENTS[ingredientName.trim().toLowerCase()] || fallback
}

const KITCHEN_INGREDIENTS = {
  'apple cider cabbage slaw': 'Ensalada de repollo con sidra de manzana',
  'beets': 'Remolachas',
  'blackened chicken': 'Pollo ennegrecido',
  'blackened salmon': 'Salmón ennegrecido',
  'broccoli': 'Brócoli',
  'caramelized onions': 'Cebollas caramelizadas',
  'cauliflower': 'Coliflor',
  'dried apricots': 'Albaricoques secos',
  'dried cranberries': 'Arándanos rojos secos',
  'fresh blackberries': 'Moras frescas',
  'fresh blueberries': 'Arándanos frescos',
  'fresh raspberries': 'Frambuesas frescas',
  'fresh strawberries': 'Fresas frescas',
  'garlic herb shrimp': 'Camarones con ajo y hierbas',
  'goat cheese (firm aged)': 'Queso de cabra (curado firme)',
  'house pickled vegetable medley': 'Mezcla de vegetales encurtidos de la casa',
  'lacinato kale': 'Col rizada lacinato',
  'lemon herb chicken': 'Pollo con limón y hierbas',
  'marinated tofu': 'Tofu marinado',
  'marry me chicken': 'Pollo Marry Me',
  'mushrooms': 'Champiñones',
  'orzo pasta': 'Pasta orzo',
  'pickled cucumbers': 'Pepinos encurtidos',
  'pickled red onions': 'Cebollas moradas encurtidas',
  'rainbow carrots': 'Zanahorias arcoíris',
  'red seedless grapes': 'Uvas rojas sin semillas',
  'rigatoni': 'Rigatoni',
  'roasted broccoli': 'Brócoli asado',
  'roasted chickpeas': 'Garbanzos asados',
  'roasted pearl onions': 'Cebollas perla asadas',
  'roasted red peppers': 'Pimientos rojos asados',
  'roasted sweet potatoes': 'Batatas asadas',
  'shaved celery': 'Apio laminado',
  'short rib': 'Costilla de res',
  'shredded purple cabbage': 'Repollo morado rallado',
  'smash burger ground beef': 'Carne molida para Smash Burger',
  'spiced pickled apple': 'Manzana encurtida con especias',
  'toasted pumpkin seeds': 'Semillas de calabaza tostadas',
  'tri-color quinoa': 'Quinua tricolor',
  'undressed shredded kale': 'Col rizada rallada sin aderezo',
  'wild rice': 'Arroz silvestre',
  'zucchini': 'Calabacín',
}

// Translate a UI label using the COMMON_TERMS dict fetched from the server.
// Falls back to the English source if missing.
const WORKFLOW_TERMS = {
  'Chef work': 'Trabajo del chef',
  'Chef status': 'Estado del chef',
  'Proteins': 'Proteínas',
  'Carbs': 'Carbohidratos',
  'Sauces': 'Salsas',
  'Vegetables': 'Verduras',
  'Toppings & bases': 'Coberturas y bases',
  'Side bases': 'Bases de acompañamientos',
  'Broths': 'Caldos',
  'Broth portions': 'Porciones de caldo',
  'Open the scaled recipe for the batch quantity and ingredients. Mark complete when the broth is ready.': 'Abre la receta ajustada para ver la cantidad del lote y los ingredientes. Marca como completo cuando el caldo esté listo.',
  'Wellness shots': 'Shots de bienestar',
  'Seasonings': 'Condimentos',
  'Other': 'Otros',
  'Current-menu recipes': 'Recetas del menú actual',
  'All recipes for this menu, grouped by category. Open a card to view, print, or edit the scaled recipe.': 'Todas las recetas de este menú, agrupadas por categoría. Abra una tarjeta para ver, imprimir o editar la receta escalada.',
  'Open a scaled recipe and check off the cooking. Workers: enter the total cooked weight after chopping, then confirm cut and weighed. Weights save to Admin and plating.': 'Abra la receta escalada y marque la cocción. Equipo: ingrese el peso cocido total después de cortar, luego confirme cortado y pesado. Los pesos se guardan en Administración y emplatado.',
  'Cooked / chopped weight': 'Peso cocido / cortado',
  'Prep team · after cooking and chopping': 'Equipo de preparación · después de cocinar y cortar',
  'Cut and weighed': 'Cortado y pesado',
  'Confirm cut and weighed': 'Confirmar cortado y pesado',
  'Needs weight': 'Falta el peso',

  'Components & quantities': 'Componentes y cantidades',
  'Saved progress could not load. Retry before editing.': 'No se pudo cargar el progreso guardado. Reintente antes de editar.',
  'Reset all kitchen progress': 'Restablecer todo el progreso de cocina',
  "Friday prep": "Preparación del viernes",
  "Saturday sides": "Acompañamientos del sábado",
  "Chef handoff": "Entrega del chef",
  "Meal order": "Orden de comidas",
  "Production week": "Semana de producción",
  "Counts updated": "Cantidades actualizadas",
  "No import recorded": "Sin importación registrada",
  "Refresh kitchen": "Actualizar cocina",
  "Kitchen data could not refresh. Check the connection before using these quantities.": "No se pudo actualizar la cocina. Revise la conexión antes de usar estas cantidades.",
  "entries not saved": "entradas sin guardar",
  "Loading kitchen…": "Cargando cocina…",
  "Choose a production week.": "Seleccione una semana de producción.",
  "Prep team: confirm the chef’s handoff. Shots are made Saturday. Sauces are check-only; follow the batch recipe.": "Equipo de preparación: confirme la entrega del chef. Los shots se hacen el sábado. Las salsas solo se confirman; siga la receta del lote.",
  "oz is the standard sauce portion.": "oz es la porción estándar de salsa.",
  "Find task or person": "Buscar tarea o persona",
  "need attention": "necesitan atención",
  "ready": "listos",
  "Ready": "Listos",
  "Not started": "Sin empezar",
  "In progress": "En curso",
  "Blocked": "Bloqueado",
  "Complete": "Completo",
  "portions · make Friday": "porciones · preparar el viernes",
  "salad portions": "porciones de ensalada",
  "Salad portions": "Porciones de ensalada",
  "Salad chicken · cut separately": "Pollo para ensalada · cortar por separado",
  "Cooked for salad cut": "Cocido para el corte de ensalada",
  "Cook and reserve this amount for the salad cut.": "Cocine y reserve esta cantidad para el corte de ensalada.",
  "Keep separate from": "Mantener separado de",
  "entree portions": "porciones de plato principal",
  "portion not set": "porción sin definir",
  "Short": "Falta",
  "All tasks ready.": "Todas las tareas están listas.",
  "Make Friday · finish Saturday": "Preparar el viernes · terminar el sábado",
  "Start with · raw": "Empezar con · crudo",
  "Need to reach · cooked": "Meta · cocido",
  "Cooked weight so far": "Peso cocido hasta ahora",
  "Make Friday": "Preparar el viernes",
  "portions": "porciones",
  "Make and chill the base. Saturday toppings and assembly are tracked separately.": "Prepare y enfríe la base. Los toppings y el montaje del sábado se registran por separado.",
  "Follow the scaled batch recipe. Confirm the batch is made; no sauce weigh-in is needed.": "Siga la receta escalada. Confirme que el lote está preparado; no es necesario pesar la salsa.",
  "oz per meal is standard.": "oz por comida es lo estándar.",
  "Chef makes Saturday": "El chef prepara el sábado",
  "bundles": "paquetes",
  "Prepare or pull this topping, then mark it complete.": "Prepare o retire este topping y márquelo completo.",
  "Still short": "Todavía falta",
  "This stays in attention even if the batch is marked complete.": "Sigue requiriendo atención aunque el lote esté marcado completo.",
  "Task status": "Estado de la tarea",
  "Working on it": "Responsable",
  "Name (optional)": "Nombre (opcional)",
  "Handoff note": "Nota de entrega",
  "What is left or blocking this?": "¿Qué falta o qué lo bloquea?",
  "No tasks in this view.": "No hay tareas en esta vista.",
  "Saved": "Guardado",
  "Saving…": "Guardando…",
  "Not saved": "Sin guardar",
  "Enter the cooked weight before marking this batch complete.": "Ingrese el peso cocido antes de completar este lote.",
  "containers to finish Saturday": "envases para terminar el sábado",
  "Chef-made · plate Saturday": "Hecho por el chef · emplatar el sábado",
  "The chef makes broths and wellness shots in Chef work. Use these quantities to fill containers.": "El chef prepara los caldos y los shots de bienestar en Trabajo del chef. Use estas cantidades para llenar los envases.",
  "broth containers to fill": "envases de caldo para llenar",
  "bundles to pack": "paquetes para empacar",
  "shots": "shots",
  "Friday base complete": "Base del viernes completa",
  "Friday base not confirmed": "Base del viernes sin confirmar",
  "Saturday toppings": "Toppings del sábado",
  "Topping ready": "Topping listo",
  "per container": "por envase",
  "total": "total",
  "Portion not set — check recipe": "Porción sin definir — revise la receta",
  "Assembly complete · reopen": "Montaje completo · reabrir",
  "Confirm containers assembled": "Confirmar envases montados",
  "Counts increased since assembly. Reopen and confirm the additional containers.": "Las cantidades aumentaron después del montaje. Reabra y confirme los envases adicionales.",
  "Counts increased. Confirm the additional portions before completing this task again.": "Las cantidades aumentaron. Confirme las porciones adicionales antes de completar otra vez.",
  "Preparation method": "Método de preparación",
  "Cooking method": "Método de cocción",
  "Use the ingredient quantities above for this batch. Instructions below describe the method; any original batch numbers are reference only.": "Use las cantidades de ingredientes de arriba para este lote. Las instrucciones describen el método; las cantidades originales son solo una referencia.",
  "Meal order changed on another device. Reload before saving.": "El orden cambió en otro dispositivo. Actualice antes de guardar.",
  "Set the sequence for this week. The saved order appears on the kitchen iPad.": "Defina la secuencia de esta semana. El orden guardado aparece en el iPad de cocina.",
  "Follow the order set by Admin. Use this list to stage the next meals and update the whiteboard.": "Siga el orden de Admin. Use la lista para preparar las próximas comidas y actualizar la pizarra.",
  "Save order": "Guardar orden",
  "Print order": "Imprimir orden",
  "Loading meal order…": "Cargando orden…",
  "Reload saved order": "Cargar orden guardado",
  "Unsaved changes — the kitchen still sees the last saved order.": "Cambios sin guardar — la cocina ve el último orden guardado.",
  "Suggested order — Admin has not confirmed it yet.": "Orden sugerido — Admin aún no lo confirma.",
  "The menu changed. Review the added or removed meals and save the order again.": "El menú cambió. Revise las comidas añadidas o eliminadas y vuelva a guardar.",
  "total meals · includes customs": "comidas totales · incluye pedidos personalizados",
  "Move up": "Subir",
  "Move down": "Bajar"
}

export function trLabel(isSpanish, translations, english) {
  if (!isSpanish) return english
  return WORKFLOW_TERMS[english] ?? translations?.labels?.[english] ?? english
}
